/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、 lint 警告の前提 (= subtree 破棄)
 * に該当しない。 renderRowActions も同様。
 */
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { DemandForecastRow } from '@ebalance/proto/bffuser/v1/demand_service_pb'
import { WorkflowCaseStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router'

import { Link, TertiaryButton, Text } from '@enechain/ecloud-designsystem'

import { MasterDataTable } from '~/components/common/MasterDataTable'
import type { MasterDataTableColumn } from '~/components/common/MasterDataTable'
import { WorkflowCaseStatusBadge } from '~/components/common/WorkflowCaseStatusBadge'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { openOrNavigate } from '~/libs/openOrNavigate'
import { convertAreaLabel } from '~/models/area'
import { formatDateTimeFull } from '~/models/dateTime'
import { decimalToNumber } from '~/models/decimal'
import { WORKFLOW_ID_PARAM } from '~/models/workflowIdParam'
import { formatProtoDate } from '~/pages/DemandForecastList/models/tabDefinitions'
import type { ForecastTableColumnKey } from '~/pages/DemandForecastList/models/tabDefinitions'
import { routePaths } from '~/routes/routePaths'

function formatCompletedAt(
  ts: Parameters<typeof timestampDate>[0] | undefined,
): string {
  if (ts === undefined) return '—'
  return formatDateTimeFull(timestampDate(ts))
}

function formatCreatedAt(
  ts: Parameters<typeof timestampDate>[0] | undefined,
): string {
  if (ts === undefined) return '—'
  return formatDateTimeFull(timestampDate(ts))
}

function formatDecimalMw(d: Parameters<typeof decimalToNumber>[0]): string {
  const n = decimalToNumber(d)
  if (n === undefined) return '—'
  return n.toLocaleString('ja-JP', { maximumFractionDigits: 3 })
}

/**
 * 数値列ヘッダの単位 (例: 「(MW)」) を本体ラベルより小さく (xs) 表示する。
 * design `.dfl-table .num small { font-size: var(--font-size-xs); color: mid-emphasis; margin-left: 4px }`
 * 準拠。 i18n の単一文字列 (例: 「ピーク需要(MW)」) を末尾の「(...)」 で label / unit に分割し、
 * 単位だけ小さくする (色・太さは th から継承 = mid-emphasis / 600)。
 */
const MetricHeader = ({ text }: { text: string }): React.ReactNode => {
  const matched = /^(.+?)(\s*\([^)]*\))$/.exec(text)
  if (matched === null) return text
  return (
    <>
      {matched[1]}
      <Text as="span" marginLeft={1} color="Text.midEmphasis" fontSize="xs">
        {matched[2]}
      </Text>
    </>
  )
}

type Props = {
  rows: DemandForecastRow[]
  isLoading?: boolean
}

export const ForecastTable = ({
  rows,
  isLoading = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const resolveDisplayName = useMemberDisplayName()
  /**
   * 需要想定詳細への遷移先 URL を構築する (編集 / 参照用)。
   * /demand/forecast-daily?workflowId=<id>&area=<areaCode>[&readonly=1]
   *
   * area クエリパラメータを付与することで、DemandForecastDaily 画面が
   * 一覧から遷移した際に対象エリアを自動選択できる。
   * readonly=1 は COMPLETE ステータス行の参照専用遷移に付与する。
   */
  const getDailyPath = (
    caseId: string,
    areaCode: number,
    readonly = false,
  ): string => {
    const base = routePaths.demandForecastDaily.path
    const qs = readonly
      ? `${WORKFLOW_ID_PARAM}=${caseId}&area=${String(areaCode)}&readonly=1`
      : `${WORKFLOW_ID_PARAM}=${caseId}&area=${String(areaCode)}`
    return `${base}?${qs}`
  }

  /**
   * 業務ワークフロー詳細 (= WorkflowCase 詳細) への遷移先 URL。
   * /workflow/cases/:caseId
   * DELETED ステータス行のクリックで使用する。
   */
  const getWorkflowCasePath = (caseId: string): string =>
    routePaths.workflowCaseDetail.path.replace(':caseId', caseId)

  /**
   * ステータスに応じた行クリック / 詳細ボタンの遷移先 URL を返す。
   *   NOT_STARTED / IN_PROGRESS → 編集画面 (forecast-daily)
   *   COMPLETE                  → 参照専用編集画面 (forecast-daily + readonly=1)
   *   DELETED                   → ワークフロー詳細 (/workflow/cases/:caseId)
   */
  function getRowDetailPath(row: DemandForecastRow): string {
    switch (row.status) {
      case WorkflowCaseStatus.COMPLETE:
        return getDailyPath(row.caseId, row.areaCode, true)
      case WorkflowCaseStatus.DELETED:
        return getWorkflowCasePath(row.caseId)
      default:
        // NOT_STARTED / IN_PROGRESS → 編集画面
        return getDailyPath(row.caseId, row.areaCode, false)
    }
  }

  // 全列定義 (表示順、全タブ共通表示)。
  // 「断面」列は issue #4927 で削除済み (新規単日 ID は 1 文字 D 形式で、旧区別も表示しない。
  // spec AC-DFL-006)。 行の断面属性 (row.section) は押せる・押せないの判断にも使わない
  // (spec AC-DFL-002「単日タブの断面区別廃止」、 issue #12069)。
  const allColumns: Array<
    MasterDataTableColumn<DemandForecastRow> & { key: ForecastTableColumnKey }
  > = [
    // ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    // 旧実装は 6 番目 (BGコード名称 の後ろ) だったが、
    // design (`design/demand/forecast-list.html`) は既に先頭に置いており、
    // 本変更で design 準拠に揃う。
    {
      key: 'status',
      header: t('page:demandForecastList.column.status'),
      cell: (row) => <WorkflowCaseStatusBadge status={row.status} />,
    },
    {
      key: 'workflow',
      align: 'left',
      nowrap: true,
      format: 'number',
      header: t('page:demandForecastList.column.workflow'),
      cell: (row) =>
        row.workflowId ? (
          // DS Link を被せて hover 下線・focus ring を design `.ecl-text-link`
          // と揃える。色は DS 既定 (Text.accentPrimary) が design 値と同じため
          // 指定しない。
          // 字面はワークフロー一覧 (`WorkflowCases/components/CaseTable` の
          // `workflowId` 列) と揃える (2026-08-27 デザイナー指摘)。 同じ
          // ワークフロー ID が画面によって別の字送りで出ていたため、
          // 等幅・medium の個別指定をやめ、 桁揃え (tabular-nums) と
          // 折り返し抑止は列オプション (`format` / `nowrap`) 側に寄せた。
          <Link asChild>
            <RouterLink
              onClick={(e) => {
                e.stopPropagation()
              }}
              to={getWorkflowCasePath(row.caseId)}
            >
              {row.workflowId}
            </RouterLink>
          </Link>
        ) : (
          '—'
        ),
    },
    {
      key: 'targetPeriod',
      header: t('page:demandForecastList.column.targetPeriod'),
      cell: (row) => formatProtoDate(row.targetDate),
    },
    {
      key: 'area',
      header: t('page:demandForecastList.column.area'),
      cell: (row) => convertAreaLabel(row.areaCode),
    },
    {
      // BG は「BG コード」「BG 名称」 の 2 列 (2026-09-01 デザイナー指摘)。
      //
      // 経緯: 元は独立 2 列 (「BGコード」/「BGコード名称」) → 2026-08-28 の PdM 判断
      // (AC-DFL-006 / spec PR #6748) で「BG」 1 列へ統合 → 本変更で再び 2 列。
      // 統合時の理由は「同じ BG コードが 1 行に 2 回出る」 ことで、 当時の 2 列目が
      // 「BG コード**名称**」 = コードと名称の両方を含む列だったため。 本変更の
      // 2 列目は **名称のみ**なのでその重複は起きない。
      //
      // ⚠️ AC-DFL-006 [P0] は現時点でまだ「1 列内に併記」 のまま。 再判断は
      // issue #6952 で PdM に確認中で、 **解決前にマージしないこと**。
      key: 'bgCode',
      header: t('page:demandForecastList.column.bgCode'),
      // **太字にしない** (2026-09-02 デザイナー判断)。 コードを label (md/600) に
      // していたのは、 1 列併記時代に**同じセルの中で**コードと名称の主従を付ける
      // ためだった (当初は名称を low-emphasis にして色で付けていたが、 薄すぎて
      // 読みにくいというデザイナー指摘 2026-08-28 を受け、 主従を色から太さへ移した)。
      //
      // 列を分けた本変更では、 **列見出し「BG コード」「BG 名称」 が区別を担う**ため
      // セル内で主従を付ける理由が消える。 広域マスタ (`admin/occto.html` は元から
      // 「BGコード」「BG名称」 の 2 列) も値を太字にしておらず、 2 列構成の先例と
      // 揃う。 コード↔名称の間隔 8px も 1 列併記時代の指定なので併せて不要。
      cell: (row) =>
        row.bgCode === undefined || row.bgCode === '' ? (
          '—'
        ) : (
          <Text as="span" textStyle="tableCell" fontFamily="mono">
            {row.bgCode}
          </Text>
        ),
    },
    {
      // 2026-09-01 デザイナー指摘「dev 全体で『BG コード・名称』 は 2 つのカラムに
      // 分けて表示 (『BG コード』『BG 名称』)」。
      //
      // ⚠️ **spec AC-DFL-006 [P0] と衝突する変更**。 同 AC は「BG コードと BG コード
      // 名称を 1 列内に併記」 と規定しており、 2026-08-28 の PdM 判断
      // (`demand-forecast-list.md` Discussion 表) が根拠。 当時 2 列をやめた理由は
      // 「同じ BG コードが 1 行に 2 回出る」 ことで、 旧 2 列目が「BG コード名称」 =
      // コードと名称の両方を含む列だったため。 本変更の 2 列目は **名称のみ**なので
      // その重複は起きず、 8080 の命名規約 (コードのみ = 「BGコード」 / 名称のみ =
      // 「BG名称」 / 併記 = 「BG コード・名称」) とも整合する。
      // AC の再判断は issue #6952 で PdM に確認中。 **解決前にマージしないこと。**
      key: 'bgName',
      header: t('page:demandForecastList.column.bgName'),
      cell: (row) =>
        row.bgName === undefined || row.bgName === '' ? (
          '—'
        ) : (
          // title は省略表示された時に全文を出すため (UI 辞書 table.md §2.1
          // `.cell-bgname[title]` と同じ責務。 同 doc の React 例どおり)
          <Text
            as="span"
            textStyle="tableCell"
            color="Text.highEmphasis"
            title={row.bgName}
          >
            {row.bgName}
          </Text>
        ),
    },
    {
      key: 'createdAt',
      header: t('page:demandForecastList.column.createdAt'),
      cell: (row) => formatCreatedAt(row.createdAt),
    },
    {
      key: 'completedAt',
      header: t('page:demandForecastList.column.completedAt'),
      cell: (row) => formatCompletedAt(row.completedAt),
    },
    {
      // #6538: completedBy is the confirming operator's eauth member ID
      // (= auth0 subject), resolved to a display name via useMemberDisplayName.
      // 生の member ID は出さない (解決不可時は '—')。
      key: 'completedBy',
      header: t('page:demandForecastList.column.completedBy'),
      cell: (row) => resolveDisplayName(row.completedBy) ?? '—',
    },
    {
      key: 'peakDemandMw',
      header: (
        <MetricHeader text={t('page:demandForecastList.column.peakDemandMw')} />
      ),
      align: 'right',
      cell: (row) =>
        row.status === WorkflowCaseStatus.COMPLETE
          ? formatDecimalMw(row.peakDemandMw)
          : '—',
    },
    {
      key: 'energyMwh',
      header: (
        <MetricHeader text={t('page:demandForecastList.column.energyMwh')} />
      ),
      align: 'right',
      cell: (row) =>
        row.status === WorkflowCaseStatus.COMPLETE
          ? formatDecimalMw(row.energyMwh)
          : '—',
    },
    {
      key: 'eval',
      header: t('page:demandForecastList.column.eval'),
      align: 'center',
      // AC-04 (TC-C-07.1): Phase 1 は評価画面が未提供のため、評価列は
      // ステータス・recordId の有無に関わらず常に「—」を表示する。
      // Phase 2 の条件リンク実装 (COMPLETE && recordId → 確定履歴詳細) は
      // 撤去済み (設計書 §4「評価列の実装形」、issue #2662)。復元は git 履歴 +
      // spec AC-015 で可能。
      cell: () => '—',
    },
  ]

  return (
    <MasterDataTable
      columns={allColumns}
      emptyLabel={t('page:demandForecastList.list.empty')}
      getRowKey={(row) =>
        `${row.caseId}-${String(row.areaCode)}-${row.bgCode ?? ''}`
      }
      isLoading={isLoading}
      onRowClick={(row, e) => {
        openOrNavigate(navigate, getRowDetailPath(row), e)
      }}
      renderRowActions={(row) => (
        <TertiaryButton
          // 表のセル内のボタンは全て DS の M サイズ (`size="md"` = 高さ 40px)。 高さ・padding・文字を
          // 上書きしない (デザイナー判断 2026-10-30。 旧 32px 規約の置き換え。 #12635)。
          asChild
          size="md"
        >
          <RouterLink to={getRowDetailPath(row)}>
            {t('page:demandForecastList.action.detail')}
          </RouterLink>
        </TertiaryButton>
      )}
      rows={rows}
    />
  )
}
